import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_BADGES = {
  pending: { label: 'Beklemede', color: '#fef3c7', textColor: '#92400e', borderColor: '#fcd34d' },
  completed: { label: 'Onaylandı', color: '#dcfce7', textColor: '#166534', borderColor: '#86efac' },
  failed: { label: 'Reddedildi', color: '#fee2e2', textColor: '#991b1b', borderColor: '#fca5a5' },
  refunded: { label: 'İade Edildi', color: '#f3f4f6', textColor: '#374151', borderColor: '#d1d5db' }
};

const PAYMENT_METHODS = {
  credit_card: { label: 'Kredi Kartı', icon: '💳' },
  bank_transfer: { label: 'Havale/EFT', icon: '🏦' }
};

// Native Modal Component
const NativeModal = ({ isOpen, onClose, title, titleColor, children, disabled }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !disabled) {
          onClose();
        }
      }}
      data-testid="modal-backdrop"
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          width: '100%',
          maxWidth: '450px',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: titleColor || '#111827',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {title}
          </h2>
        </div>
        
        {/* Content */}
        <div style={{ padding: '24px' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Native Button
const NativeButton = ({ children, onClick, variant = 'primary', disabled, fullWidth, style = {} }) => {
  const variants = {
    primary: { backgroundColor: '#2563eb', color: 'white', border: 'none' },
    success: { backgroundColor: '#16a34a', color: 'white', border: 'none' },
    danger: { backgroundColor: '#dc2626', color: 'white', border: 'none' },
    outline: { backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db' },
    outlineDanger: { backgroundColor: 'white', color: '#dc2626', border: '1px solid #fca5a5' }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '10px 20px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.2s',
        width: fullWidth ? '100%' : 'auto',
        ...variants[variant],
        ...style
      }}
    >
      {children}
    </button>
  );
};

// Badge Component
const Badge = ({ children, status }) => {
  const info = STATUS_BADGES[status] || STATUS_BADGES.pending;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: info.color,
      color: info.textColor,
      border: `1px solid ${info.borderColor}`
    }}>
      {children}
    </span>
  );
};

export default function PaymentApprovalsPage() {
  const { user, getEffectiveRole, getEffectiveSiteId, isSuperAdmin } = useAuth();
  
  // Efektif rol kontrolü - simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const isEffectiveSuperAdmin = effectiveRole === 'super_admin';
  
  const [activeTab, setActiveTab] = useState('payments'); // 'payments' veya 'managers'
  const [payments, setPayments] = useState([]);
  const [managerAssignments, setManagerAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');
  const [sites, setSites] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [reviewingAssignment, setReviewingAssignment] = useState(null);

  // Fetch sites for filter
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const token = localStorage.getItem('hims_token');
        const response = await fetch(`${API_URL}/api/sites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          let data = await response.json();
          // Hesap Yöneticisi simülasyonunda sadece kendi sitesi
          if (isEffectiveHesapYoneticisi && effectiveSiteId) {
            data = (data || []).filter(s => s.id === effectiveSiteId);
            setSiteFilter(effectiveSiteId); // Otomatik kendi sitesini seç
          }
          setSites(data);
        }
      } catch (err) {
        console.error('Failed to fetch sites:', err);
      }
    };
    fetchSites();
  }, [isEffectiveHesapYoneticisi, effectiveSiteId]);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hims_token');
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      
      const response = await fetch(`${API_URL}/api/license-payments${statusParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data);
      } else {
        toast.error('Ödemeler yüklenemedi');
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Yönetici Atama Taleplerini Getir
  const fetchManagerAssignments = useCallback(async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/manager-assignments?status=${filter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setManagerAssignments(data);
      }
    } catch (err) {
      console.error('Failed to fetch manager assignments:', err);
    }
  }, [filter]);

  useEffect(() => {
    if (activeTab === 'managers') {
      fetchManagerAssignments();
    }
  }, [activeTab, fetchManagerAssignments]);

  // Yönetici Atama Onay/Red
  const handleAssignmentReview = async (assignmentId, approved) => {
    setReviewingAssignment(assignmentId);
    
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/manager-assignments/${assignmentId}/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          approved,
          note: adminNotes 
        })
      });
      
      if (response.ok) {
        toast.success(approved ? 'Yönetici atama onaylandı' : 'Yönetici atama reddedildi');
        fetchManagerAssignments();
        setAdminNotes('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'İşlem başarısız');
      }
    } catch (err) {
      toast.error('Bağlantı hatası');
    } finally {
      setReviewingAssignment(null);
    }
  };

  const handleStatusUpdate = async (paymentId, newStatus) => {
    setProcessingId(paymentId);
    
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/license-payments/${paymentId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: newStatus,
          notes: adminNotes 
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        toast.success(result.message);
        fetchPayments();
        setShowConfirmModal(null);
        setAdminNotes('');
      } else {
        toast.error(result.detail || 'İşlem başarısız');
      }
    } catch (err) {
      toast.error('Bağlantı hatası');
    } finally {
      setProcessingId(null);
    }
  };

  const closeModal = () => {
    if (processingId) return;
    setShowConfirmModal(null);
    setAdminNotes('');
  };

  const filteredPayments = payments.filter(p => {
    // Site filter
    if (siteFilter !== 'all') {
      const siteName = p.license_info?.site_name?.toLowerCase() || '';
      if (!siteName.includes(siteFilter.toLowerCase())) return false;
    }
    
    // Search filter
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      p.license_key?.toLowerCase().includes(search) ||
      p.license_info?.license_key?.toLowerCase().includes(search) ||
      p.license_info?.site_name?.toLowerCase().includes(search) ||
      p.card_holder_name?.toLowerCase().includes(search) ||
      p.created_by_email?.toLowerCase().includes(search)
    );
  });

  const pendingCount = payments.filter(p => p.status === 'pending').length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid #2563eb', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }} data-testid="payment-approvals-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Onay Bekleyenler</h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0' }}>Ödeme ve yönetici atama taleplerini yönetin</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb', paddingBottom: '0' }}>
        <button
          onClick={() => setActiveTab('payments')}
          style={{
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: '500',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            borderBottom: activeTab === 'payments' ? '2px solid #2563eb' : '2px solid transparent',
            color: activeTab === 'payments' ? '#2563eb' : '#6b7280',
            marginBottom: '-1px'
          }}
        >
          💳 Ödeme Onayları
          {payments.filter(p => p.status === 'pending').length > 0 && (
            <span style={{ marginLeft: '8px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '9999px', fontSize: '12px' }}>
              {payments.filter(p => p.status === 'pending').length}
            </span>
          )}
        </button>
        {/* Yönetici Atamaları sekmesi - sadece Super Admin görebilir */}
        {isEffectiveSuperAdmin && isSuperAdmin() && (
        <button
          onClick={() => setActiveTab('managers')}
          style={{
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: '500',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            borderBottom: activeTab === 'managers' ? '2px solid #2563eb' : '2px solid transparent',
            color: activeTab === 'managers' ? '#2563eb' : '#6b7280',
            marginBottom: '-1px'
          }}
        >
          👤 Yönetici Atamaları
          {managerAssignments.filter(a => a.status === 'pending').length > 0 && (
            <span style={{ marginLeft: '8px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '9999px', fontSize: '12px' }}>
              {managerAssignments.filter(a => a.status === 'pending').length}
            </span>
          )}
        </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'payments' ? (
        <>
          {/* Ödeme Filters */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          {/* Site Filter Dropdown - Hesap Yöneticisi için gizli */}
          {!isEffectiveHesapYoneticisi && (
          <select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              minWidth: '150px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">Tüm Siteler</option>
            {sites.map(site => (
              <option key={site.id} value={site.name}>{site.name}</option>
            ))}
          </select>
          )}
          
          {/* Search */}
          <input
            type="text"
            placeholder="Lisans, site veya kart sahibi ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: '1 1 250px',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              minWidth: '200px'
            }}
          />
          
          {/* Status Filter */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { key: 'pending', label: '⏳ Bekleyen' },
              { key: 'all', label: 'Tümü' },
              { key: 'completed', label: '✅ Onaylı' },
              { key: 'failed', label: '❌ Reddedilen' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  backgroundColor: filter === f.key ? '#2563eb' : 'white',
                  color: filter === f.key ? 'white' : '#374151',
                  border: filter === f.key ? 'none' : '1px solid #d1d5db'
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          
          {/* Refresh */}
          <button
            onClick={fetchPayments}
            style={{
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            🔄
          </button>
        </div>
      </div>

      {/* Payments List */}
      {filteredPayments.length === 0 ? (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '48px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
          <p style={{ color: '#6b7280' }}>
            {filter === 'pending' ? 'Bekleyen ödeme bulunmuyor' : 'Ödeme kaydı bulunamadı'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredPayments.map((payment) => {
            const statusInfo = STATUS_BADGES[payment.status] || STATUS_BADGES.pending;
            const methodInfo = PAYMENT_METHODS[payment.payment_method] || PAYMENT_METHODS.bank_transfer;
            const isPending = payment.status === 'pending';
            
            return (
              <div 
                key={payment.id} 
                style={{
                  backgroundColor: isPending ? '#fffbeb' : 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  border: isPending ? '1px solid #fcd34d' : '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
                data-testid={`payment-${payment.id}`}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' }}>
                  {/* Left: Payment Info */}
                  <div style={{ flex: 1, minWidth: '300px' }}>
                    {/* Status & Method */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <Badge status={payment.status}>
                        {payment.status === 'completed' ? '✓' : payment.status === 'failed' ? '✗' : '⏳'} {statusInfo.label}
                      </Badge>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db'
                      }}>
                        {methodInfo.icon} {methodInfo.label}
                      </span>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>
                        {formatDate(payment.created_at)}
                      </span>
                    </div>
                    
                    {/* License & Payment Details */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                      <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Lisans</p>
                        <p style={{ fontFamily: 'monospace', fontWeight: '600', marginBottom: '4px' }}>
                          {payment.license_info?.license_key || payment.license_key}
                        </p>
                        <p style={{ fontSize: '13px', color: '#4b5563' }}>
                          🏢 {payment.license_info?.site_name || 'Bireysel'}
                        </p>
                      </div>
                      
                      <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Ödeme Bilgileri</p>
                        <p style={{ fontWeight: '500', marginBottom: '4px' }}>
                          👤 {payment.card_holder_name || '-'}
                        </p>
                        {payment.card_last_four && (
                          <p style={{ fontSize: '13px', color: '#4b5563' }}>
                            💳 **** {payment.card_last_four}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Amounts */}
                    <div style={{ display: 'flex', gap: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
                      <div>
                        <p style={{ fontSize: '11px', color: '#6b7280' }}>USD Tutar</p>
                        <p style={{ fontSize: '18px', fontWeight: 'bold' }}>${(payment.amount_usd || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '11px', color: '#6b7280' }}>Kur</p>
                        <p style={{ fontSize: '14px', fontWeight: '500' }}>₺{(payment.exchange_rate || 0).toLocaleString('tr-TR', { minimumFractionDigits: 4 })}</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '11px', color: '#6b7280' }}>TL Tutar</p>
                        <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#166534' }}>₺{(payment.amount_try || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                    
                    {/* Notes */}
                    {payment.notes && (
                      <p style={{ fontSize: '13px', color: '#4b5563', fontStyle: 'italic', marginTop: '12px' }}>
                        📝 Not: {payment.notes}
                      </p>
                    )}
                    {payment.admin_notes && (
                      <p style={{ fontSize: '13px', color: '#2563eb', marginTop: '8px' }}>
                        👤 Admin: {payment.admin_notes}
                      </p>
                    )}
                    
                    {/* Created By */}
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px' }}>
                      Oluşturan: {payment.created_by_email}
                    </p>
                  </div>
                  
                  {/* Right: Actions */}
                  {isPending && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '150px' }}>
                      <NativeButton
                        variant="success"
                        onClick={() => setShowConfirmModal({ payment, action: 'approve' })}
                        disabled={processingId === payment.id}
                        fullWidth
                      >
                        {processingId === payment.id ? '⏳' : '✓'} Onayla
                      </NativeButton>
                      <NativeButton
                        variant="outlineDanger"
                        onClick={() => setShowConfirmModal({ payment, action: 'reject' })}
                        disabled={processingId === payment.id}
                        fullWidth
                      >
                        ✗ Reddet
                      </NativeButton>
                    </div>
                  )}
                  
                  {/* Completed Info */}
                  {payment.status === 'completed' && payment.updated_at && (
                    <div style={{ textAlign: 'right', minWidth: '150px' }}>
                      <p style={{ fontSize: '13px', color: '#16a34a', fontWeight: '500' }}>
                        ✓ Onaylandı
                      </p>
                      <p style={{ fontSize: '11px', color: '#6b7280' }}>
                        {formatDate(payment.updated_at)}
                      </p>
                      {payment.updated_by_email && (
                        <p style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {payment.updated_by_email}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation Modal */}
      <NativeModal
        isOpen={!!showConfirmModal}
        onClose={closeModal}
        title={showConfirmModal?.action === 'approve' ? (
          <><span style={{ color: '#16a34a' }}>✓ Ödemeyi Onayla</span></>
        ) : (
          <><span style={{ color: '#dc2626' }}>✗ Ödemeyi Reddet</span></>
        )}
        titleColor={showConfirmModal?.action === 'approve' ? '#16a34a' : '#dc2626'}
        disabled={!!processingId}
      >
        {showConfirmModal && (
          <div>
            <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
              {showConfirmModal.action === 'approve' 
                ? 'Bu ödemeyi onayladığınızda lisans otomatik olarak 1 yıl uzatılacaktır.'
                : 'Bu ödemeyi reddettiğinizde lisans uzatılmayacaktır.'
              }
            </p>
            
            <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', marginBottom: '16px' }}>
              <p style={{ fontFamily: 'monospace', fontSize: '13px', marginBottom: '4px' }}>
                {showConfirmModal.payment.license_info?.license_key || showConfirmModal.payment.license_key}
              </p>
              <p style={{ fontSize: '20px', fontWeight: 'bold' }}>
                ₺{(showConfirmModal.payment.amount_try || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>
                Not (Opsiyonel)
              </label>
              <input
                type="text"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Admin notu ekleyin..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <NativeButton
                variant="outline"
                onClick={closeModal}
                disabled={!!processingId}
                fullWidth
              >
                İptal
              </NativeButton>
              <NativeButton
                variant={showConfirmModal.action === 'approve' ? 'success' : 'danger'}
                onClick={() => handleStatusUpdate(
                  showConfirmModal.payment.id, 
                  showConfirmModal.action === 'approve' ? 'completed' : 'failed'
                )}
                disabled={!!processingId}
                fullWidth
              >
                {processingId ? '⏳ İşleniyor...' : showConfirmModal.action === 'approve' ? '✓ Onayla' : '✗ Reddet'}
              </NativeButton>
            </div>
          </div>
        )}
      </NativeModal>
        </>
      ) : (
        /* Yönetici Atama Tab İçeriği */
        <div>
          {/* Filter */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['pending', 'approved', 'rejected', 'all'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: filter === f ? '1px solid #2563eb' : '1px solid #d1d5db',
                    backgroundColor: filter === f ? '#eff6ff' : 'white',
                    color: filter === f ? '#2563eb' : '#374151',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {f === 'pending' ? 'Bekleyen' : f === 'approved' ? 'Onaylanan' : f === 'rejected' ? 'Reddedilen' : 'Tümü'}
                </button>
              ))}
            </div>
          </div>

          {/* Yönetici Atama Listesi */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            {managerAssignments.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <p style={{ fontSize: '48px', margin: '0 0 16px' }}>👤</p>
                <p style={{ color: '#6b7280', margin: 0 }}>
                  {filter === 'pending' ? 'Bekleyen yönetici atama talebi yok' : 'Gösterilecek talep yok'}
                </p>
              </div>
            ) : (
              <div>
                {managerAssignments.map((assignment) => (
                  <div 
                    key={assignment.id}
                    style={{
                      padding: '20px',
                      borderBottom: '1px solid #e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '16px'
                    }}
                  >
                    <div style={{ flex: '1 1 300px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600' }}>
                          {assignment.user_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontWeight: '600' }}>{assignment.user_name}</p>
                          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>{assignment.user_email}</p>
                        </div>
                      </div>
                      <p style={{ margin: '8px 0 0', fontSize: '14px' }}>
                        <span style={{ color: '#6b7280' }}>Hesap:</span>{' '}
                        <strong>{assignment.site_name}</strong>
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                        Talep eden: {assignment.requested_by_name} • {new Date(assignment.created_at).toLocaleString('tr-TR')}
                      </p>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Badge status={assignment.status === 'pending' ? 'pending' : assignment.status === 'approved' ? 'completed' : 'failed'}>
                        {assignment.status === 'pending' ? 'Beklemede' : assignment.status === 'approved' ? 'Onaylandı' : 'Reddedildi'}
                      </Badge>
                      
                      {assignment.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <NativeButton 
                            variant="success" 
                            onClick={() => handleAssignmentReview(assignment.id, true)}
                            disabled={reviewingAssignment === assignment.id}
                          >
                            {reviewingAssignment === assignment.id ? '⏳' : '✓'} Onayla
                          </NativeButton>
                          <NativeButton 
                            variant="danger"
                            onClick={() => handleAssignmentReview(assignment.id, false)}
                            disabled={reviewingAssignment === assignment.id}
                          >
                            {reviewingAssignment === assignment.id ? '⏳' : '✗'} Reddet
                          </NativeButton>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
